import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import DonutLargeOutlinedIcon from '@mui/icons-material/DonutLargeOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { useAuth0 } from '@auth0/auth0-react';
import { useSelector } from 'react-redux';
import { track } from '../analytics/analytics';
import { PR } from './shared';

const INTRO_VERSION = 1;
export const portfolioRiskIntroKey = (subId) => `crediarc_portfolio_risk_intro_v${INTRO_VERSION}_${subId || 'anon'}`;
export const shouldShowPortfolioRiskIntro = ({ isAdmin, subId, seen }) => !isAdmin && Boolean(subId) && !seen;

const FEATURES = [
  { icon: Groups2OutlinedIcon, title: 'Choose the portfolio', description: 'Analyze prospects using requested and recommended limits, or switch to accounts receivable.' },
  { icon: DonutLargeOutlinedIcon, title: 'Find concentration risk', description: 'See client, country, and industry concentrations before they become material exposures.' },
  { icon: ScienceOutlinedIcon, title: 'Test a crisis', description: 'Model credit demand, limit pressure, collections deterioration, and potential loss by client.' },
  { icon: AutoAwesomeOutlinedIcon, title: 'Explain the result', description: 'Use the AI assistant to understand calculations, assumptions, and the clients driving risk.' },
];

export default function PortfolioRiskIntroduction() {
  const { user } = useAuth0();
  const isAdmin = useSelector((state) => state.userInfo.isAdmin);
  const subId = user?.sub || (typeof window !== 'undefined' ? localStorage.getItem('subId') : null);
  const [open, setOpen] = useState(false);
  const checked = useRef(false);

  useEffect(() => {
    if (checked.current || isAdmin || !subId) return undefined;
    let seen = false;
    try { seen = Boolean(localStorage.getItem(portfolioRiskIntroKey(subId))); } catch { /* show once for this mount */ }
    checked.current = true;
    if (!shouldShowPortfolioRiskIntro({ isAdmin, subId, seen })) return undefined;

    let timer;
    const showWhenClear = () => {
      if (document.querySelector('[role="dialog"]')) {
        timer = window.setTimeout(showWhenClear, 400);
        return;
      }
      try { localStorage.setItem(portfolioRiskIntroKey(subId), 'shown'); } catch { /* storage can be unavailable */ }
      setOpen(true);
      track('portfolio_risk_intro_shown', { feature: 'Portfolio Risk Intelligence', version: INTRO_VERSION });
    };
    timer = window.setTimeout(showWhenClear, 350);
    return () => window.clearTimeout(timer);
  }, [isAdmin, subId]);

  const close = (action) => {
    try { localStorage.setItem(portfolioRiskIntroKey(subId), action); } catch { /* no-op */ }
    setOpen(false);
    track(action === 'started' ? 'portfolio_risk_intro_started' : 'portfolio_risk_intro_dismissed', {
      feature: 'Portfolio Risk Intelligence', version: INTRO_VERSION,
    });
  };

  return <Dialog open={open} onClose={() => close('dismissed')} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}>
    <Box sx={{ position: 'relative', px: { xs: 3, sm: 4 }, pt: 4, pb: 3, color: '#fff', background: `linear-gradient(135deg, ${PR.ink} 0%, #173D72 65%, ${PR.blue} 145%)` }}>
      <IconButton aria-label="Close Portfolio Risk introduction" onClick={() => close('dismissed')} sx={{ position: 'absolute', top: 12, right: 12, color: 'rgba(255,255,255,.88)' }}><CloseIcon /></IconButton>
      <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', opacity: .8 }}>New intelligence workspace</Typography>
      <Typography sx={{ mt: 1, fontSize: { xs: 23, sm: 27 }, fontWeight: 800, lineHeight: 1.15 }}>Meet Portfolio Risk Intelligence</Typography>
      <Typography sx={{ mt: 1.1, maxWidth: 500, fontSize: 14, lineHeight: 1.55, opacity: .9 }}>Turn the client data already in CrediArc into forward-looking portfolio decisions—across prospects and accounts receivable.</Typography>
    </Box>
    <Box sx={{ px: { xs: 3, sm: 4 }, py: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
      {FEATURES.map(({ icon: Icon, title, description }) => <Box key={title} sx={{ display: 'flex', gap: 1.4, alignItems: 'flex-start' }}>
        <Box sx={{ display: 'grid', placeItems: 'center', width: 36, height: 36, flexShrink: 0, borderRadius: '10px', bgcolor: PR.blueSoft, color: PR.blue }}><Icon sx={{ fontSize: 19 }} /></Box>
        <Box><Typography sx={{ color: PR.ink, fontSize: 13.5, fontWeight: 750 }}>{title}</Typography><Typography sx={{ mt: .25, color: PR.muted, fontSize: 12, lineHeight: 1.45 }}>{description}</Typography></Box>
      </Box>)}
    </Box>
    <Box sx={{ px: { xs: 3, sm: 4 }, py: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, borderTop: `1px solid ${PR.line}`, bgcolor: '#FAFBFD' }}>
      <Typography sx={{ color: PR.muted, fontSize: 11.5 }}>You can change the portfolio basis at any time.</Typography>
      <Button variant="contained" onClick={() => close('started')} data-track-label="Start exploring Portfolio Risk" sx={{ textTransform: 'none', fontWeight: 750, borderRadius: '9px', px: 2.5, bgcolor: PR.blue }}>Start exploring</Button>
    </Box>
  </Dialog>;
}
